import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BUILTIN_PERSONAL_FIELDS, Field, MAX_PERSONAL_FIELDS, MAX_STARRED_FIELDS } from '@cloudsign/shared';
import type { FieldScope, FieldTypeInfo, Group } from '@cloudsign/shared';
import { Lock, Plus, Star } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Badge, Button, Card, CheckboxField, PageHeader, Spinner } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { SortableList } from '../../components/SortableList';
import { FieldList as FieldListSchema, fieldsKey, fieldsPath, useFieldList, useFieldTypes, useGroupRecord, useSiteGroups, useSiteRecord } from './api';
import type { FieldTarget } from './api';
import { FieldDialog, typeLabel } from './FieldDialog';

function FieldList({
  target,
  scope,
  types,
  siteGroups,
  canManage,
  atLimit,
  addLabel,
  emptyText,
  extra,
}: {
  target: FieldTarget;
  scope: FieldScope;
  types: FieldTypeInfo[];
  siteGroups: Group[];
  canManage: boolean;
  /** Personal fields: true once the group has 15 fields including the built-ins. */
  atLimit?: boolean;
  addLabel: string;
  emptyText: string;
  extra?: ReactNode;
}) {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState<{ field?: Field } | null>(null);
  const list = useFieldList(target, scope, showArchived);
  const all = list.data ?? [];
  const active = all.filter((f) => f.status === 'active').sort((a, b) => a.sortOrder - b.sortOrder);
  const archived = all.filter((f) => f.status === 'archived');
  const starredCount = active.filter((f) => f.starred).length;
  const scopeName = t(`fnUi.fields.scope.${scope}`);
  const key = [...fieldsKey(target, scope), showArchived];

  const reorder = useMutation({
    mutationFn: (next: Field[]) =>
      api.put(`${fieldsPath(target)}/order`, {
        ...(target.kind === 'site' ? { query: { scope } } : {}),
        body: { fieldIds: next.map((f) => f.id) },
        schema: FieldListSchema,
      }),
    onMutate: (next) => {
      qc.setQueryData(key, [...next.map((f, i) => ({ ...f, sortOrder: i + 1 })), ...archived]);
    },
    onError: () => qc.invalidateQueries({ queryKey: fieldsKey(target, scope) }),
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api.patch(`${fieldsPath(target)}/${v.id}`, { body: v.body, schema: Field }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fields', target.kind, target.id] }),
  });

  const info = (f: Field) => types.find((x) => x.key === f.typeKey);

  const starButton = (f: Field) => {
    const ti = info(f);
    const reason = f.sensitive
      ? t('fnUi.fields.starSensitive')
      : ti && !ti.canBeStarred
        ? t('fnUi.fields.starNotAllowed')
        : !f.starred && starredCount >= MAX_STARRED_FIELDS
          ? t('fnUi.fields.starTooMany', { max: MAX_STARRED_FIELDS })
          : '';
    if (!canManage) {
      return f.starred ? (
        <span title={t('fnUi.fields.unstar', { name: f.label })} className="inline-flex h-9 w-9 items-center justify-center text-amber-600">
          <Star aria-hidden="true" className="h-4 w-4 fill-current" />
          <span className="sr-only">{t('fnUi.fields.unstar', { name: f.label })}</span>
        </span>
      ) : null;
    }
    return (
      <button
        type="button"
        aria-pressed={f.starred}
        aria-label={f.starred ? t('fnUi.fields.unstar', { name: f.label }) : t('fnUi.fields.star', { name: f.label })}
        title={reason || undefined}
        disabled={Boolean(reason) && !f.starred}
        onClick={() => patch.mutate({ id: f.id, body: { starred: !f.starred } })}
        className={`inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-40 ${f.starred ? 'text-amber-600' : 'text-slate-600'}`}
      >
        <Star aria-hidden="true" className={`h-4 w-4 ${f.starred ? 'fill-current' : ''}`} />
      </button>
    );
  };

  return (
    <Card
      title={scopeName}
      description={t('fnUi.fields.starredCounter', { count: starredCount, max: MAX_STARRED_FIELDS })}
      className="mb-6"
    >
      {extra}
      {list.isLoading ? <Spinner /> : null}
      {list.error ? <ErrorPanel error={list.error} onRetry={() => void list.refetch()} /> : null}
      {!list.isLoading && !list.error && active.length === 0 ? <p className="py-3 text-sm text-slate-600">{emptyText}</p> : null}
      {active.length ? (
        <SortableList
          items={active}
          getKey={(f) => f.id}
          getLabel={(f) => f.label}
          label={t('fnUi.fields.listLabel', { scope: scopeName })}
          disabled={!canManage || reorder.isPending}
          onChange={(next) => reorder.mutate(next)}
          renderItem={(f) => {
            const ti = info(f);
            return (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{f.label}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge>{ti ? typeLabel(ti) : f.typeKey}</Badge>
                    {f.required ? <Badge tone="warn">{t('fnUi.badge.required')}</Badge> : null}
                    {f.sensitive ? (
                      <Badge tone="bad">
                        <Lock aria-hidden="true" className="mr-1 inline h-3 w-3" />
                        {t('fnUi.badge.sensitive')}
                      </Badge>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  {starButton(f)}
                  {canManage ? <Button aria-label={t('fnUi.fields.edit', { name: f.label })} onClick={() => setDialog({ field: f })}>{t('common.edit')}</Button> : null}
                </div>
              </div>
            );
          }}
        />
      ) : null}
      <ErrorLine error={reorder.error ?? patch.error} />
      {reorder.isError ? <p className="mt-2 text-sm text-red-700">{t('fnUi.fields.reorderFailed')}</p> : null}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <CheckboxField label={t('fnUi.fields.showArchived')} checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
        {canManage ? (
          <div className="text-right">
            <Button variant="primary" disabled={atLimit} onClick={() => setDialog({})}>
              <Plus aria-hidden="true" className="h-4 w-4" />
              {addLabel}
            </Button>
            {atLimit ? <p className="mt-1 text-xs text-slate-600">{t('fnUi.fields.limitReached', { max: MAX_PERSONAL_FIELDS })}</p> : null}
          </div>
        ) : null}
      </div>
      {showArchived && archived.length ? (
        <div className="mt-4">
          <h3 className="mb-2 text-sm font-medium text-slate-800">{t('fnUi.fields.archivedList')}</h3>
          <ul className="space-y-2">
            {archived.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm text-slate-700">
                <span>
                  {f.label} <Badge>{t('fnUi.badge.archived')}</Badge>
                </span>
                {canManage ? (
                  <Button aria-label={t('fnUi.fields.restore', { name: f.label })} disabled={atLimit} onClick={() => patch.mutate({ id: f.id, body: { status: 'active' } })}>
                    {t('fnUi.fields.restore', { name: '' }).trim()}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {dialog ? <FieldDialog target={target} scope={scope} types={types} siteGroups={siteGroups} {...(dialog.field ? { field: dialog.field } : {})} onClose={() => setDialog(null)} /> : null}
    </Card>
  );
}

/** /manage/sites/:id/fields */
export function SiteFieldsPage() {
  const { id = '' } = useParams();
  const canManage = useCan('sites.manage');
  const site = useSiteRecord(id);
  const types = useFieldTypes();
  const groups = useSiteGroups(id);
  const target: FieldTarget = { kind: 'site', id };
  const name = site.data?.name ?? '';
  if (site.error || types.error) return <ErrorPanel error={site.error ?? types.error} onRetry={() => void site.refetch()} />;
  return (
    <div>
      <p className="mb-2 text-sm">
        <Link to={`/manage/sites/${id}`} className="text-brand underline">
          {t('fnUi.fields.backToSite', { site: name })}
        </Link>
      </p>
      <PageHeader title={t('fnUi.fields.siteTitle', { site: name })} description={t('fnUi.fields.siteDescription')} />
      {site.isLoading || types.isLoading ? (
        <Spinner />
      ) : (
        (['sign_in', 'sign_out'] as const).map((scope) => (
          <FieldList
            key={scope}
            target={target}
            scope={scope}
            types={types.data ?? []}
            siteGroups={groups.data ?? []}
            canManage={canManage}
            addLabel={t('fnUi.fields.add')}
            emptyText={t('fnUi.fields.empty')}
          />
        ))
      )}
    </div>
  );
}

/** /manage/groups/:id/fields */
export function GroupFieldsPage() {
  const { id = '' } = useParams();
  const canManage = useCan('groups.manage');
  const group = useGroupRecord(id);
  const types = useFieldTypes();
  const target: FieldTarget = { kind: 'group', id };
  const list = useFieldList(target, 'personal', false);
  const used = BUILTIN_PERSONAL_FIELDS.length + (list.data ?? []).filter((f) => f.status === 'active').length;
  const name = group.data?.name ?? '';
  if (group.error || types.error) return <ErrorPanel error={group.error ?? types.error} onRetry={() => void group.refetch()} />;
  const builtIns = (
    <div className="mb-4">
      <p className="mb-2 text-sm font-medium text-slate-800" data-testid="field-counter">
        {t('fnUi.fields.counter', { count: used, max: MAX_PERSONAL_FIELDS })}
      </p>
      <ul aria-label={t('fnUi.fields.builtIn')} className="space-y-2">
        {BUILTIN_PERSONAL_FIELDS.map((k) => (
          <li key={k} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm">
            <span className="flex items-center gap-2 pl-2">
              <Lock aria-hidden="true" className="h-4 w-4 text-slate-500" />
              {t(`fnUi.fields.builtIn.${k}`)}
            </span>
            <Badge>{t('fnUi.fields.builtIn')}</Badge>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <div>
      <p className="mb-2 text-sm">
        <Link to={`/manage/groups/${id}`} className="text-brand underline">
          {t('fnUi.fields.backToGroup', { group: name })}
        </Link>
      </p>
      <PageHeader title={t('fnUi.fields.groupTitle', { group: name })} description={t('fnUi.fields.groupDescription')} />
      {group.isLoading || types.isLoading ? (
        <Spinner />
      ) : (
        <FieldList
          target={target}
          scope="personal"
          types={types.data ?? []}
          siteGroups={[]}
          canManage={canManage}
          atLimit={used >= MAX_PERSONAL_FIELDS}
          addLabel={t('fnUi.fields.addPersonal')}
          emptyText={t('fnUi.fields.emptyPersonal')}
          extra={builtIns}
        />
      )}
    </div>
  );
}
